import { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useUnreadChannelsCount } from '../lib/messaging';
import { supabase } from '../lib/supabase';
import { billingIsLocked } from '../lib/billing';
import logoColor from '../assets/White Border Logo.png';

/* ─── Tab bar icons (24×24 stroke SVGs) ─────────────────────────────── */
const HomeIcon = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>;
const CalendarIcon = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
const StarIcon = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>;
const CardIcon = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>;
const ActivityIcon = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>;
const AwardIcon = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/></svg>;
const PackageIcon = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>;
const PeopleIcon = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>;
const ClipboardIcon = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/></svg>;
const PhoneIcon = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 10.8 19.79 19.79 0 01.22 2.18 2 2 0 012.18 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 7.91a16 16 0 006.16 6.16l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>;
const ShieldIcon = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
const SignOutIcon = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>;

function getTabIcon(to) {
  if (to.includes('/dashboard')) return HomeIcon;
  if (to.includes('/races')) return CalendarIcon;
  if (to.includes('/recruiting')) return StarIcon;
  if (to.includes('/billing')) return CardIcon;
  if (to.includes('/standards')) return AwardIcon;
  if (to.includes('/gear')) return PackageIcon;
  if (to.includes('/athletes')) return PeopleIcon;
  if (to.includes('/plan-builder')) return ClipboardIcon;
  if (to.includes('/calls')) return PhoneIcon;
  if (to.includes('/admin') || to.includes('/coaches') || to.includes('/pro-athletes')) return ShieldIcon;
  return HomeIcon;
}

const SHORT_LABELS = {
  'Race Calendar': 'Races',
  'Plan Builder': 'Plans',
  'Pro Athletes': 'Pro',
  'Training': 'Training',
};
function getShortLabel(label) {
  return SHORT_LABELS[label] ?? label;
}

function BottomTabBar({ links, isBillingLocked }) {
  const location = useLocation();
  return (
    <nav className="bottom-tab-bar">
      {links.map(link => {
        const isBillingTab = link.to.includes('/billing');
        const isDisabled = isBillingLocked && !isBillingTab;
        const base = link.to.replace(/\/[a-f0-9-]{36}$/, '');
        const isActive = location.pathname === link.to ||
          (base !== link.to && location.pathname.startsWith(base)) ||
          (link.to === location.pathname);
        const icon = getTabIcon(link.to);
        const label = getShortLabel(link.label);
        if (isDisabled) {
          return (
            <span key={link.label} className="tab-item tab-item--disabled" title="Pay your balance to unlock">
              <span className="tab-icon">{icon}</span>
              <span className="tab-label">{label}</span>
            </span>
          );
        }
        return (
          <NavLink
            key={link.label}
            to={link.to}
            className={({ isActive: navActive }) => {
              const active = navActive || (base !== link.to && location.pathname.startsWith(base));
              return `tab-item${active ? ' tab-item--active' : ''}${link.admin ? ' tab-item--admin' : ''}`;
            }}
          >
            <span className="tab-icon">{icon}</span>
            <span className="tab-label">{label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

const ROLE_LABEL = { coach: 'Coach', admin: 'Coach', athlete: 'Athlete', parent: 'Parent' };

function navInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);
}

function getNavLinks() {
  const lastAthlete = sessionStorage.getItem('apex_last_athlete');
  const lastCoach = sessionStorage.getItem('apex_last_coach');
  const lastProAthlete = sessionStorage.getItem('apex_last_pro_athlete');
  const athletesTo = lastAthlete ? `/coach/athletes/${lastAthlete}` : '/coach/athletes';
  const coachesTo = lastCoach ? `/admin/coaches/${lastCoach}` : '/admin/coaches';
  const proAthletesTo = lastProAthlete ? `/admin/pro-athletes/${lastProAthlete}` : '/admin/pro-athletes';
  return {
    coach: [
      { to: '/coach/dashboard', label: 'Dashboard' },
      { to: athletesTo, label: 'Athletes' },
      { to: '/coach/plan-builder', label: 'Plan Builder' },
      { to: '/coach/calls', label: 'Calls' },
      { to: '/coach/messages', label: 'Messages' },
      { to: '/coach/recruiting', label: 'Recruiting' },
    ],
    admin: [
      { to: '/admin/dashboard', label: 'Admin' },
      { to: coachesTo, label: 'Coaches' },
      { to: proAthletesTo, label: 'Pro Athletes' },
      { to: '/admin/safety-review', label: 'Safety Review' },
      { to: '/coach/dashboard', label: 'Dashboard' },
      { to: athletesTo, label: 'Athletes' },
      { to: '/coach/plan-builder', label: 'Plan Builder' },
      { to: '/coach/calls', label: 'Calls' },
      { to: '/coach/messages', label: 'Messages' },
      { to: '/coach/recruiting', label: 'Recruiting' },
    ],
  athlete: [
    { to: '/athlete/dashboard', label: 'Dashboard' },
    { to: '/athlete/races', label: 'Race Calendar' },
    { to: '/athlete/recruiting', label: 'Recruiting' },
    { to: '/athlete/billing', label: 'Billing' },
  ],
  pro: [
    { to: '/pro/dashboard', label: 'Training' },
    { to: '/pro/races', label: 'Race Calendar' },
    { to: '/pro/standards', label: 'Standards' },
    { to: '/pro/gear', label: 'Gear' },
  ],
  parent: [
    { to: '/parent/dashboard', label: 'Dashboard' },
    { to: '/parent/billing', label: 'Billing' },
  ],
  };
}

export function Layout({ children }) {
  const { profile, signOut } = useAuth();
  // Drives the gold dot on the "Messages" sidebar link. Quietly
  // returns 0 when the user has no profile (auth screens) so the
  // hook stays safe to mount unconditionally.
  const { count: unreadChannelsCount } = useUnreadChannelsCount(profile?.id);
  const navigate = useNavigate();
  const location = useLocation();
  const [isBillingLocked, setIsBillingLocked] = useState(false);

  // Check billing lock status on mount and when profile changes
  useEffect(() => {
    if (!profile) return;
    const role = profile.role;
    if (role !== 'athlete' && role !== 'parent') { setIsBillingLocked(false); return; }

    (async () => {
      const now = new Date();
      const month = now.getMonth() + 1;
      const year = now.getFullYear();

      if (role === 'athlete') {
        const { data } = await supabase
          .from('billing')
          .select('paid_at')
          .eq('athlete_id', profile.id)
          .eq('month', month)
          .eq('year', year)
          .maybeSingle();
        setIsBillingLocked(billingIsLocked(data));
      } else if (role === 'parent') {
        const { data: links } = await supabase
          .from('parent_athlete_links')
          .select('athlete_id')
          .eq('parent_id', profile.id);
        if (!links?.length) { setIsBillingLocked(false); return; }
        const ids = links.map(l => l.athlete_id);
        const { data: bills } = await supabase
          .from('billing')
          .select('athlete_id, paid_at')
          .in('athlete_id', ids)
          .eq('month', month)
          .eq('year', year);
        const anyLocked = ids.some(id => {
          const bill = (bills ?? []).find(b => b.athlete_id === id);
          return billingIsLocked(bill);
        });
        setIsBillingLocked(anyLocked);
      }
    })();
  }, [profile]);
  const [recruitingEligible, setRecruitingEligible] = useState(() => sessionStorage.getItem('apex_recruiting_eligible') === '1');
  const [navAvatarUrl, setNavAvatarUrl] = useState(() => sessionStorage.getItem('apex_nav_avatar') || null);
  const [isProAthlete, setIsProAthlete] = useState(() => sessionStorage.getItem('apex_is_pro') === '1');

  useEffect(() => {
    if (!profile?.id) return;
    // Clear cached avatar when user changes
    setNavAvatarUrl(null);
    sessionStorage.removeItem('apex_nav_avatar');
    if (profile.role === 'coach' || profile.role === 'admin') {
      supabase.from('coach_profiles').select('avatar_url').eq('id', profile.id).maybeSingle()
        .then(({ data }) => { if (data?.avatar_url) { setNavAvatarUrl(data.avatar_url); sessionStorage.setItem('apex_nav_avatar', data.avatar_url); } });
    } else if (profile.role === 'athlete') {
      supabase.from('athlete_profiles').select('avatar_url').eq('id', profile.id).maybeSingle()
        .then(({ data }) => { if (data?.avatar_url) { setNavAvatarUrl(data.avatar_url); sessionStorage.setItem('apex_nav_avatar', data.avatar_url); } });
    }
  }, [profile?.id, profile?.role]);

  useEffect(() => {
    if (profile?.role !== 'athlete') {
      setIsProAthlete(false); sessionStorage.removeItem('apex_is_pro');
      setRecruitingEligible(false); sessionStorage.removeItem('apex_recruiting_eligible');
      return;
    }
    supabase.from('athlete_profiles').select('grade_level').eq('id', profile.id).maybeSingle()
      .then(({ data: ap }) => {
        if (ap?.grade_level === 'pro') {
          setIsProAthlete(true);
          sessionStorage.setItem('apex_is_pro', '1');
          return;
        }
        if (!ap?.grade_level || !['10th', '11th', '12th'].includes(ap.grade_level)) return;
        supabase.from('billing').select('tier').eq('athlete_id', profile.id)
          .order('year', { ascending: false }).order('month', { ascending: false }).limit(1)
          .then(({ data: billing }) => {
            if (billing?.[0]?.tier === 'elite') { setRecruitingEligible(true); sessionStorage.setItem('apex_recruiting_eligible', '1'); }
          });
      });
  }, [profile?.id]);

  async function handleSignOut() {
    await signOut();
    navigate('/login');
  }

  const dashboardPath = profile
    ? (profile.role === 'admin' ? '/coach/dashboard' : isProAthlete ? '/pro/dashboard' : `/${profile.role}/dashboard`)
    : '/login';

  const NAV_LINKS = getNavLinks();
  let links = profile
    ? (profile.role === 'athlete' && isProAthlete ? NAV_LINKS.pro : (NAV_LINKS[profile.role] ?? []))
    : [];

  // For regular athletes, hide Recruiting unless eligible (10th–12th grade + Elite plan)
  if (profile?.role === 'athlete' && !isProAthlete) {
    links = links.filter(l => l.to !== '/athlete/recruiting' || recruitingEligible);
  }

  // Coaches with the is_admin flag get the Admin + Coaches + Pro Athletes tabs appended (gold styling)
  if (profile?.is_admin && profile.role !== 'admin') {
    links = [
      ...links,
      { to: '/admin/dashboard',    label: 'Admin',        admin: true },
      { to: NAV_LINKS.admin[1].to, label: 'Coaches',      admin: true },
      { to: NAV_LINKS.admin[2].to, label: 'Pro Athletes', admin: true },
    ];
  }

  return (
    <div className="page">
      <nav className="nav">
        <div className="nav-inner">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Link to={dashboardPath} className="nav-logo">
              <img src={logoColor} alt="Apex Elite" style={{ height: 36 }} />
            </Link>
            {links.length > 1 && (
              <div className="nav-links">
                {links.map(link => {
                  const base = link.to.replace(/\/[a-f0-9-]{36}$/, '');
                  const isBillingTab = link.to.includes('/billing');
                  const isDisabled = isBillingLocked && !isBillingTab;
                  if (isDisabled) {
                    return (
                      <span
                        key={link.label}
                        className={`nav-link nav-link--disabled${link.admin ? ' nav-link--admin' : ''}`}
                        title="Pay your balance to unlock"
                      >
                        {link.label}
                      </span>
                    );
                  }
                  const isMessages = link.to === '/coach/messages';
                  return (
                    <NavLink
                      key={link.label}
                      to={link.to}
                      className={({ isActive }) => {
                        const active = isActive || (base !== link.to && window.location.pathname.startsWith(base));
                        return `nav-link${active ? ' active' : ''}${link.admin ? ' nav-link--admin' : ''}`;
                      }}
                    >
                      {link.label}
                      {isMessages && unreadChannelsCount > 0 ? (
                        <span
                          aria-label={`${unreadChannelsCount} unread`}
                          style={{
                            display: 'inline-block',
                            marginLeft: 6,
                            width: 8,
                            height: 8,
                            borderRadius: 4,
                            background: 'var(--gold, #FCC625)',
                            verticalAlign: 'middle',
                          }}
                        />
                      ) : null}
                    </NavLink>
                  );
                })}
              </div>
            )}
          </div>
          <div className="nav-right">
            {profile && (() => {
              const avatar = (
                <div className="nav-avatar">
                  {navAvatarUrl
                    ? <img src={navAvatarUrl} alt={profile.full_name} />
                    : navInitials(profile.full_name)}
                </div>
              );
              const label = isProAthlete ? 'APEX ELITE PRO' : (ROLE_LABEL[profile.role] ?? profile.role);
              const inner = (
                <>
                  {avatar}
                  <div className="nav-user">
                    <p className="nav-user-name">{profile.full_name}</p>
                    <p className="nav-user-role" style={isProAthlete ? { color: 'var(--gold)', fontWeight: 700, fontSize: 10, letterSpacing: '0.08em' } : undefined}>{label}</p>
                  </div>
                </>
              );
              if (profile.role === 'coach' || profile.role === 'admin') {
                return <Link to="/coach/settings" className="nav-user-wrap nav-user--link">{inner}</Link>;
              }
              if (profile.role === 'athlete' && isProAthlete) {
                return <Link to="/pro/profile" className="nav-user-wrap nav-user--link">{inner}</Link>;
              }
              if (profile.role === 'athlete') {
                return <Link to="/athlete/settings" className="nav-user-wrap nav-user--link">{inner}</Link>;
              }
              return <div className="nav-user-wrap">{inner}</div>;
            })()}
            <button className="nav-signout nav-signout--text" onClick={handleSignOut}>Sign Out</button>
            <button className="nav-signout nav-signout--icon" onClick={handleSignOut} title="Sign Out" aria-label="Sign Out">
              {SignOutIcon}
            </button>
          </div>
        </div>
      </nav>
      <div className="page-content">
        {isBillingLocked && !location.pathname.includes('/billing') && !location.pathname.includes('/settings') ? (
          <div className="billing-lockout">
            <div className="billing-lockout-icon">🔒</div>
            <div className="billing-lockout-title">Training Locked</div>
            <p className="billing-lockout-body">
              Your coaching payment is overdue. Please pay your balance to restore access.
            </p>
            <Link
              to={profile?.role === 'parent' ? '/parent/billing' : '/athlete/billing'}
              className="btn btn-primary"
              style={{ width: 'auto', padding: '10px 28px', display: 'inline-block', textDecoration: 'none' }}
            >
              Go to Billing
            </Link>
          </div>
        ) : children}
      </div>
      {links.length > 0 && <BottomTabBar links={links} isBillingLocked={isBillingLocked} />}
    </div>
  );
}
